import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ia } from 'lib/helpers/utility';
import { PERMISSION_DENIED } from 'lib/constants/messages';
import {
  BLOCKED_DIRECT_ADMIN_CODE,
  VIEWPORT_ACTION_DISABLED_MESSAGE
} from 'lib/constants/constants';

import { showAlert } from 'components/shared/Alert/Alert';

const DEFAULT_ERROR_TITLE = "Couldn't complete the action";
const DEFAULT_SUCCESS_TITLE = 'Action completed';
const DANGER_COLOR = 'danger';
const SUCCESS_COLOR = 'success';

export const useCustomMutation = ({
  mutationFn,
  successMessage,
  errorMessage,
  invalidateQueryKey,
  refetchQueryKey,
  onMutate,
  onSuccess = () => {},
  onSettled,
  handleResponse,
  disableAlert = false
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate,
    onSettled,
    onSuccess: (data, variables, context) => {
      switch (data?.code) {
        case 0:
          {
            onSuccess(data, variables, context);
            const message =
              typeof successMessage === 'function'
                ? successMessage(data)
                : successMessage || DEFAULT_SUCCESS_TITLE;

            if (!disableAlert && typeof successMessage !== 'function') {
              showAlert({
                title: message,
                color: data?.data?.color || SUCCESS_COLOR
              });
            }

            if (invalidateQueryKey) {
              if (ia(invalidateQueryKey)) {
                invalidateQueryKey.forEach((key) => {
                  if (ia(key)) queryClient.invalidateQueries({ queryKey: key });
                  else queryClient.invalidateQueries({ queryKey: [key] });
                });
                break;
              }

              queryClient.invalidateQueries({ queryKey: [invalidateQueryKey] });
            }

            if (refetchQueryKey) {
              if (ia(refetchQueryKey)) {
                refetchQueryKey.forEach((key) => {
                  if (ia(key)) queryClient.refetchQueries({ queryKey: key });
                  else queryClient.refetchQueries({ queryKey: [key] });
                });
                break;
              }

              queryClient.refetchQueries({ queryKey: [refetchQueryKey] });
            }
          }
          break;
        case 2:
          // A permission denial must reach the caller's error handler —
          // otherwise disableAlert makes code 2 the one response that
          // produces no feedback at all.
          if (typeof handleResponse === 'function') {
            handleResponse(data);
            break;
          }

          if (!disableAlert) {
            showAlert({
              title: data?.error || PERMISSION_DENIED,
              color: DANGER_COLOR
            });
          }
          break;
        case BLOCKED_DIRECT_ADMIN_CODE:
          showAlert({
            title: 'Action unavailable in viewport',
            message: data?.error || VIEWPORT_ACTION_DISABLED_MESSAGE,
            color: 'warning'
          });
          break;
        default: {
          if (typeof handleResponse === 'function') {
            handleResponse(data);
            break;
          }

          if (!disableAlert) {
            showAlert({
              title: errorMessage || data?.error || DEFAULT_ERROR_TITLE,
              color: DANGER_COLOR
            });
          }
          break;
        }
      }
    },
    onError: (data) => {
      if (!disableAlert) {
        showAlert({
          title: data?.error || errorMessage,
          color: DANGER_COLOR
        });
      }
    }
  });
};
